import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { makeStyles } from '@material-ui/styles';
import { withStyles } from '@material-ui/core/styles';
import {
	IconButton, Tooltip, Switch,
	FormControlLabel
} from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import EmailIcon from '@material-ui/icons/Email';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import EditIcon from '@material-ui/icons/Edit';
import Label from '../../../../../components/Label/Label.js';
import DataTable from '../../../../../components/DataTable/DataTable.js';

import { TogglePacketEditFailureDialog, SetFailurePacketValidInvalid } from '../../../actions/PacketActions';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	infoButton: {
		color: theme.palette.info.main
	}
}));

const BlueRedSwitch = withStyles((theme) => ({
	root: {
		width: 28,
		height: 16,
		padding: 0,
		display: 'flex',
	},
	switchBase: {
		padding: 2,
		color: theme.palette.error.main,
		'&$checked': {
			transform: 'translateX(12px)',
			color: theme.palette.common.white,
			'& + $track': {
				opacity: 1,
				backgroundColor: theme.palette.primary.main,
				borderColor: theme.palette.primary.main,
			},
		},
	},
	thumb: {
		width: 12,
		height: 12,
		boxShadow: 'none',
	},
	track: {
		border: `1px solid ${theme.palette.grey[500]}`,
		borderRadius: 16 / 2,
		opacity: 1,
		backgroundColor: theme.palette.common.white,
	},
	checked: {},
}))(Switch);

const PacketDataTable = (props) => {
	const { className, data, ...rest } = props;
	const dispatch = useDispatch();
	const classes = useStyles();
	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);

	const _editValid = (event, tableId) => {
		dispatch(SetFailurePacketValidInvalid(userId, tableId));
	};

	const _editFailure = (tableId, failureId) => {
		dispatch(TogglePacketEditFailureDialog(true, tableId, failureId));
	};

	const _renderValid = (tableMeta) => {
		const { rowData } = tableMeta;
		return (
			<>
				<Tooltip title="Valid">
					<FormControlLabel
						control={(
							<BlueRedSwitch
								// checked={rowData[12]}
								onChange={(event) => _editValid(event, rowData[0])}
								name="valid"
								defaultChecked={rowData[9]}
							/>
						)}
					// label="Custom color"
					/>
				</Tooltip>
			</>
		);
	};

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;
		return (
			<>
				<Tooltip title="Edit">
					<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _editFailure(rowData[0], rowData[1])}>
						<EditIcon />
					</IconButton>
				</Tooltip>
			</>
		);
	};

	let _getTableColumns = '';
	if (role === 'Client') {
		_getTableColumns = () => (
			[
				{
					name: 'id',
					label: 'Id',
					options: {
						display: false,
						filter: false,
						viewColumns: false,
						filterType: 'textField',
					}
				},
				{
					name: 'failureId',
					label: 'Failure Id',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'year',
					label: 'Year',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'week',
					label: 'Week',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'date',
					label: 'Date',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'city',
					label: 'City',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'filename',
					label: 'Log',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'initialTimestamp',
					label: 'Failure Timestamp',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'cfn1Level',
					label: 'Level 1',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'action',
					label: 'Actions',
					options: {
						filter: false,
						sort: false,
						viewColumns: false,
						empty: true,
						customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
					}
				},
			]
		);
	}	else {
		_getTableColumns = () => (
			[
				{
					name: 'id',
					label: 'Id',
					options: {
						display: false,
						filter: false,
						viewColumns: false,
						filterType: 'textField',
					}
				},
				{
					name: 'failureId',
					label: 'Failure Id',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'year',
					label: 'Year',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'week',
					label: 'Week',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'date',
					label: 'Date',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'city',
					label: 'City',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'filename',
					label: 'Log',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'initialTimestamp',
					label: 'Failure Timestamp',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'cfn1Level',
					label: 'Level 1',
					options: {
						display: true,
						filterType: 'textField',
					}
				},
				{
					name: 'valid',
					label: 'Valid',
					options: {
						display: false,
						filter: false,
						sort: false,
						viewColumns: false,
						empty: true,
						filterType: 'textField',
					}
				},
				{
					name: 'actionValid',
					label: 'Valid',
					options: {
						filter: false,
						sort: false,
						viewColumns: false,
						empty: true,
						customBodyRender: (value, tableMeta, updateValue) => _renderValid(tableMeta)
					}
				},
				{
					name: 'action',
					label: 'Actions',
					options: {
						filter: false,
						sort: false,
						viewColumns: false,
						empty: true,
						customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
					}
				},
			]
		);
	}

	const _getTableOptions = () => ({
		filterType: 'dropdown',
		selectableRows: false,
		expandableRows: false, // Try Adding This
	});

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<DataTable
				id="packetDataTble"
				title=""
				data={data}
				columns={_getTableColumns()}
				rowsPerPage={10}
				tableBodyMaxHeight="30em"
				tableBodyCellMaxWidth="250"
				options={_getTableOptions()}
			/>
		</div>
	);
};

PacketDataTable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
};

export default PacketDataTable;
